<template>
  <div class="ui grid">
    <div class="row">
      <div v-if="user.profile.role === 'Administrator'">
        <router-link :to="{
          path:
            $route.query.return === 'unpaid'
              ? '/invoices/unpaid'
              : $route.query.return === 'paid'
                ? '/invoices/paid'
                : $route.query.return === 'expiring' ? '/licenses/expiring'
                  : $route.query.return === 'expired' ? '/licenses/expired'
                    : '/school/select',
        }">
          <sui-button content="Back to list" positive icon="circle arrow left" />
        </router-link>
      </div>
      <div v-if="user.profile.role === 'TeacherAdministrator'">
        <router-link to="/">
          <sui-button content="Back" positive icon="circle arrow left" />
        </router-link>
      </div>
    </div>
    <div class="row">
      <h1 class="ui huge header">
        {{ school.name }}
      </h1>
    </div>
    <div class="ui divider"></div>
    <div class="row">
      <table class="ui striped selectable table" v-if="school">
        <tbody>
          <tr>
            <td>Roll number</td>
            <td>{{ school.rollNumber }}</td>
          </tr>
          <tr>
            <td>Status</td>
            <td v-if="school.status === 'active'" class="positive">{{ school.status }}</td>
            <td v-if="school.status === 'archived'" class="negative">{{ school.status }}</td>
          </tr>
          <tr>
            <td>Total student count</td>
            <td>{{ school.totalStudentCount }}</td>
          </tr>
          <tr>
            <td>Address line 1</td>
            <td>{{ school.address1 }}</td>
          </tr>
          <tr>
            <td>Address line 2</td>
            <td>{{ school.address2 }}</td>
          </tr>
          <tr>
            <td>Address line 3</td>
            <td>{{ school.address3 }}</td>
          </tr>
          <tr>
            <td>County</td>
            <td>{{ school.county }}</td>
          </tr>
          <tr>
            <td>Local authority</td>
            <td>{{ school.localAuthority }}</td>
          </tr>
          <tr>
            <td>Eircode</td>
            <td>{{ school.eircode }}</td>
          </tr>
          <tr>
            <td>Email address</td>
            <td>
              <a :href="`mailto:${school.emailAddress}`">{{
                school.emailAddress
              }}</a>
            </td>
          </tr>
          <tr>
            <td>Available languages</td>
            <td>
              {{
                school.availableLanguages
                  .map((l) => mapLanguageCode(l))
                  .join(', ')
              }}
            </td>
          </tr>
          <tr>
            <td>Default languages</td>
            <td>
              {{ mapLanguageCode(school.defaultLanguage) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="row" v-if="user.profile.role === 'Administrator'">
      <sui-button content="Update details" positive icon="circle pencil sign" v-on:click="showSchoolModal" />
      <sui-button v-if="school.status === 'active'" content="Archive" negative icon="" v-on:click="archiveSchool" />
      <sui-button v-if="school.status === 'archived'" content="Restore" secondary icon="window restore"
        v-on:click="restoreSchool" />
    </div>

    <portal to="modal-destination" :order="1">
      <sui-modal v-model="schoolModalOpen">
        <sui-modal-header>School details</sui-modal-header>
        <sui-modal-content class="scrolling">
          <school-form v-bind:school="school" v-bind:error="schoolError" v-on:saved="schoolSaved" create="true"
            ref="schoolForm" />
        </sui-modal-content>
        <sui-modal-actions>
          <sui-button negative v-on:click="hideSchoolModal">
            Cancel
          </sui-button>
        </sui-modal-actions>
      </sui-modal>
    </portal>

    <manage-licenses v-if="
      user.profile.role === 'Administrator' &&
      school &&
      school.id === schoolId
    " v-bind:school="school" />
    <manage-invoices v-if="
      user.profile.role === 'Administrator' &&
      school &&
      school.id === schoolId
    " v-bind:school="school" />
    <manage-teachers v-if="
      school &&
      (school.id === schoolId || user.profile.role === 'TeacherAdministrator')
    " v-bind:school="school" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ManageTeachers from '@/components/teachers/ManageTeachers';
import ManageLicenses from '@/components/licenses/ManageLicenses';
import ManageInvoices from '@/components/invoices/ManageInvoices';
import SchoolForm from '@/components/schools/SchoolForm';

export default {
  name: 'SchoolDetails',
  props: ['schoolId'],
  components: { ManageTeachers, ManageLicenses, SchoolForm, ManageInvoices },
  computed: mapGetters({
    school: 'school',
    user: 'user',
  }),
  data() {
    return {
      schoolModalOpen: false,
      schoolError: null,
    };
  },
  methods: {
    archiveSchool() {
      this.$dialog
        .confirm('Please confirm to archive this school')
        .then(() => {
          this.$store
            .dispatch('updateStatus', {
              schoolId: this.school.id,
              status: 'archived',
            })
            .then(
              () => {
                this.schoolModalOpen = false;
                this.$store.dispatch('getSchool', this.schoolId);
              },
              (error) => {
                console.error(error);
              },
            );
        })
        .catch(() => { });
    },
    restoreSchool() {
      this.$dialog
        .confirm('Please confirm to restore this school')
        .then(() => {
          this.$store
            .dispatch('updateStatus', {
              schoolId: this.school.id,
              status: 'active',
            })
            .then(
              () => {
                this.schoolModalOpen = false;
                this.$store.dispatch('getSchool', this.schoolId);
              },
              (error) => {
                console.error(error);
              },
            );
        })
        .catch(() => { });
    },
    mapLanguageCode(languageCode) {
      switch (languageCode) {
        case 'en':
          return 'English';
        case 'ga':
          return 'Irish';
        default:
          return languageCode;
      }
    },
    schoolSaved() {
      this.school.totalStudentCount = parseInt(
        this.school.totalStudentCount,
        10,
      );
      this.$store
        .dispatch('createSchool', this.school)
        .then(() => {
          this.hideSchoolModal();
        })
        .catch(() => { });
    },
    showSchoolModal() {
      this.schoolError = null;
      this.$refs.schoolForm.reset();
      this.schoolModalOpen = true;
    },
    hideSchoolModal() {
      this.schoolModalOpen = false;
      this.$store.dispatch('getSchool', this.schoolId);
    },
  },
  created() {
    this.$store.dispatch('getSchool', this.schoolId);
  },
};
</script>



// WEBPACK FOOTER //
// src/components/schools/SchoolDetails.vue